import {
  Component,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  METER_COLUMNS,
  MeterColumn,
  MeterColumnConfig,
  MeterQuery,
  MeterRow,
  MeterSensorType,
} from '@contracts';
import { DialogService, ModuleStore, ToastService } from '@core';
import { MenuHeaderTopic } from '@ui';
import { MeterFacade } from './facade/meter.facade';
import { resolveMeterColumns } from './utils/meter-column.util';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { MeterColumnUI } from './interface/meter';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { CreateMeterModalComponent } from './components/create-meter-modal/create-meter.component';
import { ManageMeterModalComponent } from './components/manage-meter-modal/manage-meter-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { MeterService } from '@feature/meter';

@Component({
  selector: 'core-meter',
  templateUrl: './meter.component.html',
  styleUrl: './meter.component.scss',
  providers: [MeterFacade],
})
export class MeterComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Input() showCreateButton: boolean = true;
  @Input() showSearch: boolean = true;
  @Input() showFilterFloor: boolean = true;
  @Input() showFilterGroup: boolean = true;
  @Input() showFilterType: boolean = true;
  @Input() showExportButton: boolean = true;
  menuTopic: MenuHeaderTopic = {
    title: 'view_value.meter',
    subtitle: 'view_value.detail_meter',
    iconUrl: 'assets/shared/icons/meter.svg',
  };
  columnState: MeterColumnUI[] = [];
  pageEvent!: PageEvent;
  pageSizeOptions = [10, 25, 50, 100];
  pageIndex = 0;
  pageSize = 10;
  sensorType: MeterSensorType = 'energy';
  dataSource = new MatTableDataSource<MeterRow>([]);
  columns: MeterColumn[] = [];
  displayedColumns: string[] = [];
  exportColumns: string[] = [];
  private module = inject(ModuleStore).module();
  sensor_type_list: MeterSensorType[] = ['energy', 'water'];

  formMeter = new FormGroup({
    sensor_type: new FormControl<MeterSensorType>('energy', {
      nonNullable: true,
    }),
  });

  searchGroupMeter: string = '';
  private currentFilter: any;
  selectedFloor: string = 'all';
  floor_list: string[] = [];

  groupControl = new FormControl('all');
  selectedGroup: string = 'all';
  group_list: any[] = [];
  floorSearch: string = '';
  groupSearch: string = '';
  floorControl = new FormControl('all');
  filteredFloorList: string[] = [];
  filteredGroupList: string[] = [];
  input = new FormControl('');

  constructor(
    private readonly facade: MeterFacade,
    private readonly meterService: MeterService,
    @Optional()
    @Inject(METER_COLUMNS)
    private readonly meterColumns: MeterColumnConfig,
    private dialog: MatDialog,
    private translate: TranslateService,
    private moduleStore: ModuleStore,
    private toastService: ToastService,
    private dialogService: DialogService,
  ) {}

  onSelectFloor(value: string) {
    this.selectedFloor = value;
    this.search();
  }

  onSelectGroup(value: string) {
    this.selectedGroup = value;
    this.search();
  }

  loadLovlocaiton() {
    this.meterService
      .getLovLocation({
        type: 'public',
        key: 'location_tier_3',
      })
      .subscribe((res) => (this.floor_list = res.meta.response_data));
  }
  allLabel = '';
  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => {
      const current = this.floorControl.value;

      this.floorControl.setValue(current);
      this.groupControl.setValue(this.groupControl.value);
    });

    this.restoreColumnState();
    this.setupColumns(this.currentSensorType);
    this.loadData();
    this.loadLovlocaiton();
    this.floorControl.valueChanges.subscribe((value) => {
      const keyword = (value || '').toLowerCase();

      this.filteredFloorList = this.floor_list.filter((f) =>
        f.toLowerCase().includes(keyword),
      );
    });

    this.groupControl.valueChanges.subscribe((value) => {
      const keyword = (value || '').toLowerCase();

      this.filteredGroupList = this.group_list.filter((g) =>
        g.toLowerCase().includes(keyword),
      );

      if (!value) {
        this.selectedGroup = 'all';
        this.search();
      }
    });

    this.formMeter.valueChanges.subscribe((value) => {
      const sensorType = (value.sensor_type ?? 'energy') as MeterSensorType;
      this.setupColumns(sensorType);
      this.loadData();
    });
  }

  get currentSensorType(): MeterSensorType {
    return this.formMeter.controls.sensor_type.value ?? 'energy';
  }

  dropColumnFromHeader(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.displayedColumns,
      event.previousIndex,
      event.currentIndex,
    );

    this.columnState = this.displayedColumns.map((key, index) => {
      const col = this.columnState.find((c) => c.key === key)!;
      return { ...col, order: index };
    });

    this.applyColumnState();
  }

  setupColumns(sensorType: MeterSensorType): void {
    const base = resolveMeterColumns(
      this.meterColumns,
      sensorType,
      this.moduleStore.module(),
    );

    this.columnState = this.mergeColumnState(base, this.columnState);

    this.applyColumnState();
  }

  private mergeColumnState(
    base: MeterColumn[],
    saved: MeterColumnUI[],
  ): MeterColumnUI[] {
    if (!saved?.length) {
      return base.map((col, index) => ({
        ...col,
        visible: col.visible ?? true,
        order: col.order ?? index,
      }));
    }

    const map = new Map(saved.map((c) => [c.key, c]));

    return base
      .map((col, index) => {
        const savedCol = map.get(col.key);

        return {
          ...col,
          visible: savedCol?.visible ?? col.visible ?? true,
          order: savedCol?.order ?? index,
        };
      })
      .sort((a, b) => a.order - b.order);
  }

  dropColumn(event: CdkDragDrop<MeterColumnUI[]>): void {
    moveItemInArray(this.columnState, event.previousIndex, event.currentIndex);

    this.columnState = this.columnState.map((c, i) => ({
      ...c,
      order: i,
    }));

    this.applyColumnState();
  }

  applyColumnState(): void {
    const sorted = [...this.columnState]
      .sort((a, b) => a.order - b.order)
      .filter((c) => c.visible);

    this.displayedColumns = sorted.map((c) => c.key);

    this.exportColumns = sorted
      .filter((c) => c.exportable !== false)
      .map((c) => c.key);
    this.columns = sorted;
    this.persistColumnState();
  }

  toggleColumn(key: string): void {
    const col = this.columnState.find((c) => c.key === key);
    if (!col) return;

    col.visible = !col.visible;
    this.applyColumnState();
  }
  private readonly STORAGE_KEY = 'meter.column.state';

  persistColumnState(): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.columnState));
  }

  restoreColumnState(): void {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (!saved) return;

    try {
      this.columnState = JSON.parse(saved);
    } catch {
      this.columnState = [];
    }
  }

  loadData(): void {
    const query: MeterQuery = {
      sensor_module: this.module,
      sensor_type: this.currentSensorType,
    };
    this.dialogService.withLoading(this.facade.load(query)).subscribe({
      next: (rows) => {
        this.dataSource = new MatTableDataSource(rows);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.setFilterPredicate();
        // this.floor_list = [
        //   ...new Set(
        //     rows
        //       .map((r) => r.location_tier_3 as string)
        //       .filter((v) => v != null && v !== ''),
        //   ),
        // ].sort((a, b) => {
        //   const numA = Number(a.match(/\d+/)?.[0] ?? Infinity);
        //   const numB = Number(b.match(/\d+/)?.[0] ?? Infinity);

        //   return numA - numB;
        // });

        this.group_list = [
          ...new Set(
            rows
              .flatMap((r) => r.tags ?? [])
              .filter((v) => v != null && v !== ''),
          ),
        ];
        this.filteredFloorList = this.floor_list;
        this.filteredGroupList = this.group_list;
        if (this.currentFilter) {
          this.dataSource.filter = JSON.stringify(this.currentFilter);
        }
        this.toastService.success('view_value.load_data_success');
      },
      error: (err) => {
        this.toastService.error('view_value.load_data_error');
      },
    });
  }

  setFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: MeterRow, filter: string) => {
      const f = JSON.parse(filter);

      const searchText = f.search?.toLowerCase() || '';
      const matchSearch =
        !searchText ||
        Object.values(data).some((val) =>
          String(val).toLowerCase().includes(searchText),
        );
      const matchGroup =
        !f.group || f.group === 'all' || !!data.tags?.includes(f.group);

      const matchFloor =
        !f.floor || f.floor === 'all' || data.location_tier_3 === f.floor;

      const matchType = !f.type || data.type === f.type;

      return matchSearch && matchGroup && matchType && matchFloor;
    };
  }

  trackColumn(_: number, column: MeterColumn): string {
    return column.key;
  }

  getCellValue(row: MeterRow, column: MeterColumn, index: number) {
    if (column.valueGetter)
      return column.valueGetter(row, index, this.pageIndex, this.pageSize);

    return row[(column.field ?? column.key) as keyof MeterRow];
  }

  getDecimalInfo(column: MeterColumn): string {
    const decimal = column.decimal;
    if (!decimal) return '1.0-0';

    const minIntegerDigits = decimal.minIntegerDigits ?? 1;
    const minFractionDigits = decimal.minFractionDigits ?? 0;
    const maxFractionDigits = decimal.maxFractionDigits ?? 0;

    return `${minIntegerDigits}.${minFractionDigits}-${maxFractionDigits}`;
  }

  reset(): void {
    this.searchGroupMeter = '';
    this.selectedFloor = '';
    this.selectedGroup = '';
    this.sensorType = 'energy';

    this.search();
  }

  search(): void {
    this.dialogService.runWithLoading(
      () => {
        this.currentFilter = {
          search: this.searchGroupMeter,
          floor: this.selectedFloor,
          group: this.selectedGroup,
          type: this.sensorType,
        };

        this.dataSource.filter = JSON.stringify(this.currentFilter);
        this.toastService.success('view_value.load_data_success');
      },
      {
        disableClose: true,
        delay: 500,
      },
    );
  }

  onPageChange(event: PageEvent): void {
    this.pageEvent = event;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  onExportCSV(): void {
    const rows = this.dataSource.filteredData?.length
      ? this.dataSource.filteredData
      : this.dataSource.data;

    const exportData = rows.map((row, index) => {
      const item: Record<string, unknown> = {};
      for (const key of this.exportColumns) {
        const column = this.columns.find((c) => c.key === key);
        if (!column) continue;
        item[key] = this.getCellValue(row, column, index);
      }
      return item;
    });

    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      useBom: true,
      headers: this.exportColumns,
    };

    new AngularCsv(exportData, 'meter-export', options);
  }

  getNumberCellValue(
    row: MeterRow,
    column: MeterColumn,
    index: number,
  ): number | string | null {
    const value = this.getCellValue(row, column, index);

    if (value === null || value === undefined || value === 'string')
      return null;
    if (typeof value === 'number' || typeof value === 'string') return value;

    return null;
  }

  getTextCellValue(
    row: MeterRow,
    column: MeterColumn,
    index: number,
  ): string | number | null {
    const value = this.getCellValue(row, column, index);
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' || typeof value === 'number') return value;

    return String(value);
  }

  getStatusCellValue(
    row: MeterRow,
    column: MeterColumn,
    index: number,
  ): string {
    const value = this.getCellValue(row, column, index);
    return value === null ? '' : String(value);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'online':
        return 'status status--online';
      case 'offline':
        return 'status status--offline';
      default:
        return 'status';
    }
  }

  getDateCellValue(
    row: MeterRow,
    column: MeterColumn,
    index: number,
  ): string | number | Date | null {
    const value = this.getCellValue(row, column, index);

    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      value instanceof Date
    ) {
      return value;
    }

    return null;
  }

  openDetailModal(data: any, editMode?: boolean) {
    const dialogRef = this.dialog.open(ManageMeterModalComponent, {
      data: {
        data,
        editMode,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadData();
      this.search();
    });
  }

  openCreateModal() {
    const dialogRef = this.dialog.open(CreateMeterModalComponent, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'updated') {
        this.loadData();
        this.search();
      }
    });
  }

  displayStr = (value: string | null): string => {
    if (value === 'all') {
      return this.translate.instant('view_value.all');
    }

    return this.translate.instant(value || '');
  };
}
